/**
 * 基础档案跨页缓存 (客商/商品/仓位/公式/司机) — 60s TTL + 并发去重 + 写后失效.
 *
 * [2026-10-08 性能] 背景: 这些档案被 10+ 个页面各自全量拉取 (listCustomers 13 处 /
 * listProducts 9 处), keep-alive 下每次切回页签整组重发, 客商上量的厂单请求就几百 KB —
 * 是"切个页面都要转半天"的主要来源. 档案数据变化频率极低 (建档/改档才变), 60s 窗口完全够用.
 *
 * 用法 (api 层双轨):
 *   - 读: listCustomersCached(params)  — 命中缓存直接返回同一份数组 (调用方不要原地修改! 见下)
 *   - 写: saveXxx/deleteXxx 内部成功后调 depsInvalidate('customers:') — 失效永不忘
 *
 * ⚠️ 返回的是共享数组引用: 调用方需要排序/过滤请复制后操作 (spread/slice),
 *    原地 sort/reverse 会污染缓存影响其他页面. (现有调用点均为只读消费, 新调用点注意.)
 *
 * 已知旁路 (不在 api 写函数里、失效覆盖不到的写路径):
 *   - 入库/出库"免选仓位"由后端自动建「{品种}默认仓」— 提交后的仓位补拉必须用
 *     强刷版 (listWareareas 原函数) 或先 invalidateWareareas().
 */

const DEPS_TTL = 60 * 1000

// key -> { at: 写入时刻, promise: 在飞/已完成的请求 promise }
const store = new Map()

/** 参数对象 → 稳定 key 片段 (浅对象按 key 排序拼接; undefined 值跳过) */
export function depsKey(prefix, params) {
  if (!params) return prefix
  const keys = Object.keys(params).filter((k) => params[k] !== undefined && params[k] !== null && params[k] !== '')
  keys.sort()
  const frag = keys.map((k) => k + '=' + String(params[k])).join('&')
  return frag ? prefix + '?' + frag : prefix
}

/** 带缓存读: TTL 内命中返回同一 promise (并发去重); 失败不缓存 (下次重试) */
export function depsCached(key, fetcher) {
  const hit = store.get(key)
  if (hit && Date.now() - hit.at < DEPS_TTL) return hit.promise
  const p = Promise.resolve()
    .then(fetcher)
    .catch((e) => {
      // 失败的响应不留缓存 (且只清自己, 防并发时误删别人的新条目)
      if (store.get(key) && store.get(key).promise === p) store.delete(key)
      throw e
    })
  store.set(key, { at: Date.now(), promise: p })
  return p
}

/** 前缀失效: 档案写操作成功后调用 (如 depsInvalidate('customers:')) */
export function depsInvalidate(prefix) {
  for (const k of Array.from(store.keys())) {
    if (k.startsWith(prefix)) store.delete(k)
  }
}
